import React, { useState } from 'react';
import {
  DotProps,
  TooltipProps,
  AreaChart,
  Text,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

import { PayoffGraph } from 'utils/shared/quantlib/StrategyBuilder';
import { roundToDecimals, formatCurrencyOrNumber } from 'utils/shared/number/roundToDecimals';
import {
  calculateReturnOnMaxRisk,
  maxRiskPercentUnavailableMessage,
  profitPercentUnavailableMessage,
} from 'utils/shared/quantlib/returnOnCost';

interface ProbabilityData {
  stockPrice: number;
  probability: number;
}
interface CustomDotProps extends DotProps {
  index: number;
}

interface PayoffChartProps {
  data?: PayoffGraph[];
  breakEvenPoints: any[];
  activeItem: string;
  currentStockPrice?: number;
  xMin: number;
  xMax: number;
  maxLoss: number | null | undefined;
  probabilityData: ProbabilityData[];
  animated?: boolean;
  /** Payoff curve at expiration, index-aligned with `data`. Drawn as a dotted reference. */
  expirationData?: PayoffGraph[];
  /** Show the dotted "at expiration" reference line (when scrubbing before expiry). */
  showExpirationReference?: boolean;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  payload?: Array<{
    name: string;
    value?: number;
    color?: string;
  }>;
}

const formatTickX = (tick: number) => `$${tick.toFixed(2)}`;
const formatTickY = (tick: number, key: string): string => {
  // return `$${new Intl.NumberFormat('en-US').format(parseInt(tick.toFixed(0), 10))}`;
  if (key === 'profit') {
    return `$${new Intl.NumberFormat('en-US').format(parseInt(tick.toFixed(0), 10))}`;
  } else if (key === 'profitPercent') {
    return `${tick.toFixed(0)}%`;
  } else if (key === 'contractValue') {
    return `$${new Intl.NumberFormat('en-US').format(parseInt(tick.toFixed(2), 10))}`;
  } else if (key === 'delta') {
    return tick.toFixed(0);
  } else if (key === 'gamma') {
    return tick.toFixed(0);
  } else if (key === 'theta') {
    return tick.toFixed(0);
  } else if (key === 'vega') {
    return tick.toFixed(0);
  } else if (key === 'rho') {
    return tick.toFixed(0);
  }
  return tick.toFixed(0);
};
// const formatLabel = (label: string, key: string) => {
//   // profit, profitPercent, contractValue, delta, gamma, theta, vega, rho
//   if (key === 'profit') {
//     return `$${label}`;
//   } else if (key === 'profitPercent') {
//     return `${label}%`;
//   } else if (key === 'contractValue') {
//     return `$${label}`;
//   } else if (key === 'delta') {
//     return label;
//   } else if (key === 'gamma') {
//     return label;
//   } else if (key === 'theta') {
//     return label;
//   } else if (key === 'vega') {
//     return label;
//   } else if (key === 'rho') {
//     return label;
//   }
//   return label;
// };

/* TODO : WE WILL REMOVE THIS (CustomTooltip) CODE BLOCK LATER */
export const CustomTooltip: React.FC<React.PropsWithChildren<CustomTooltipProps>> = (props) => {
  const { active, payload } = props;
  if (active && payload && payload.length) {
    const positiveProfit = payload.find((p) => p?.name === 'positiveProfit')?.value;
    const negativeProfit = payload.find((p) => p?.name === 'negativeProfit')?.value;
    const positiveProfitPercent = payload.find((p) => p?.name === 'positiveProfitPercent')?.value;
    const negativeProfitPercent = payload.find((p) => p?.name === 'negativeProfitPercent')?.value;
    const positiveContractValue = payload.find((p) => p?.name === 'positiveContractValue')?.value;
    const negativeContractValue = payload.find((p) => p?.name === 'negativeContractValue')?.value;
    const positivePercentOfMaxRisk = payload.find(
      (p) => p?.name === 'positivePercentOfMaxRisk',
    )?.value;
    const negativePercentOfMaxRisk = payload.find(
      (p) => p?.name === 'negativePercentOfMaxRisk',
    )?.value;
    const positiveDelta = payload.find((p) => p?.name === 'positiveDelta')?.value;
    const negativeDelta = payload.find((p) => p?.name === 'negativeDelta')?.value;
    const positiveGamma = payload.find((p) => p?.name === 'positiveGamma')?.value;
    const negativeGamma = payload.find((p) => p?.name === 'negativeGamma')?.value;
    const positiveTheta = payload.find((p) => p?.name === 'positiveTheta')?.value;
    const negativeTheta = payload.find((p) => p?.name === 'negativeTheta')?.value;
    const positiveVega = payload.find((p) => p?.name === 'positiveVega')?.value;
    const negativeVega = payload.find((p) => p?.name === 'negativeVega')?.value;
    const positiveRho = payload.find((p) => p?.name === 'positiveRho')?.value;
    const negativeRho = payload.find((p) => p?.name === 'negativeRho')?.value;

    return (
      <div className="rounded p-2 text-xs shadow">
        {!!positiveProfit && (
          <p className="text-green-500">{`Profit: $${positiveProfit?.toFixed(2)}`}</p>
        )}
        {!!negativeProfit && (
          <p className="text-red-500">{`Loss: $${negativeProfit?.toFixed(2)}`}</p>
        )}
        {!!positiveProfitPercent && (
          <p className="text-green-500">{`Profit: ${positiveProfitPercent?.toFixed(2)}%`}</p>
        )}
        {!!negativeProfitPercent && (
          <p className="text-red-500">{`Loss: ${negativeProfitPercent?.toFixed(2)}%`}</p>
        )}
        {!!positiveContractValue && (
          <p className="text-green-500">{`$${positiveContractValue?.toFixed(2)}`}</p>
        )}
        {!!negativeContractValue && (
          <p className="text-red-500">{`$${negativeContractValue?.toFixed(2)}`}</p>
        )}
        {!!positivePercentOfMaxRisk && (
          <p className="text-green-500">{`${positivePercentOfMaxRisk?.toFixed(2)}%`}</p>
        )}
        {!!negativePercentOfMaxRisk && (
          <p className="text-red-500">{`${negativePercentOfMaxRisk?.toFixed(2)}%`}</p>
        )}
        {!!positiveDelta && (
          <p className="text-green-500">{`Delta: ${positiveDelta?.toFixed(2)}`}</p>
        )}
        {!!negativeDelta && <p className="text-red-500">{`Delta: ${negativeDelta?.toFixed(2)}`}</p>}
        {!!positiveGamma && (
          <p className="text-green-500">{`Gamma: ${positiveGamma?.toFixed(2)}`}</p>
        )}
        {!!negativeGamma && <p className="text-red-500">{`Gamma: ${negativeGamma?.toFixed(2)}`}</p>}
        {!!positiveTheta && (
          <p className="text-green-500">{`Theta: ${positiveTheta?.toFixed(2)}`}</p>
        )}
        {!!negativeTheta && <p className="text-red-500">{`Theta: ${negativeTheta?.toFixed(2)}`}</p>}
        {!!positiveVega && <p className="text-green-500">{`Vega: ${positiveVega?.toFixed(2)}`}</p>}
        {!!negativeVega && <p className="text-red-500">{`Vega: ${negativeVega?.toFixed(2)}`}</p>}
        {!!positiveRho && <p className="text-green-500">{`Rho: ${positiveRho?.toFixed(2)}`}</p>}
        {!!negativeRho && <p className="text-red-500">{`Rho: ${negativeRho?.toFixed(2)}`}</p>}
      </div>
    );
  }
  return null;
};

const mapAcitveKeyToPositiveAndNegative = (activeKey: string) => {
  if (activeKey === 'profit') {
    return ['positiveProfit', 'negativeProfit'];
  } else if (activeKey === 'profitPercent') {
    return ['positiveProfitPercent', 'negativeProfitPercent'];
  } else if (activeKey === 'contractValue') {
    return ['positiveContractValue', 'negativeContractValue'];
  } else if (activeKey === 'percentOfMaxRisk') {
    return ['positivePercentOfMaxRisk', 'negativePercentOfMaxRisk'];
  } else if (activeKey === 'delta') {
    return ['positiveDelta', 'negativeDelta'];
  } else if (activeKey === 'gamma') {
    return ['positiveGamma', 'negativeGamma'];
  } else if (activeKey === 'theta') {
    return ['positiveTheta', 'negativeTheta'];
  } else if (activeKey === 'vega') {
    return ['positiveVega', 'negativeVega'];
  } else if (activeKey === 'rho') {
    return ['positiveRho', 'negativeRho'];
  }
  return ['positiveProfit', 'negativeProfit'];
};

const calculatePercentageChange = (price: number, currPrice: number) => {
  const change = ((price - currPrice) / currPrice) * 100;
  const sign = change > 0 ? '+' : '';
  return `${sign}${change?.toFixed(2)}`;
};

const formatValue = (key: string, value: number) => {
  if (
    ['positiveProfit', 'negativeProfit', 'positiveContractValue', 'negativeContractValue'].includes(
      key,
    )
  ) {
    return `$${formatCurrencyOrNumber(value)}`;
  } else if (
    [
      'positiveProfitPercent',
      'negativeProfitPercent',
      'positivePercentOfMaxRisk',
      'negativePercentOfMaxRisk',
    ].includes(key)
  ) {
    return `${value}%`;
  }
  return value;
};
const profitLossActivDot = (currentStockPrice: number) => (props: any) => {
  const { cx, cy, dataKey, value, stroke, strokeWidth, fill, payload } = props;
  const changePercentage = calculatePercentageChange(payload?.stockPrice, currentStockPrice);

  if (!cx || !cy)
    return (
      <Text
        x={props.cx}
        y={15}
        fontSize={11}
        fill={'#fff'}
        alignmentBaseline="middle"
        textAnchor="middle"
      >
        {`$${payload?.stockPrice?.toFixed(2)} (${changePercentage}%)`}
      </Text>
    );

  return (
    <>
      <circle cx={cx} cy={cy} r={6} stroke={stroke} strokeWidth={strokeWidth} fill={fill} />
      <Text
        x={props.cx + 10}
        y={props.cy + 4}
        fontSize="14"
        textAnchor="start"
        alignmentBaseline="middle"
        fill={'#fff'}
        style={{ zIndex: 50, fontWeight: 600 }}
      >
        {formatValue(dataKey, value?.[1]?.toFixed(2))}
      </Text>
    </>
  );
};

const calculateArea = (data: ProbabilityData[]) => {
  let area = 0;
  for (let i = 0; i < data.length - 1; i++) {
    const x1 = data[i].stockPrice;
    const y1 = data[i].probability;
    const x2 = data[i + 1].stockPrice;
    const y2 = data[i + 1].probability;
    area += ((y1 + y2) * (x2 - x1)) / 2;
  }
  return area;
};

const probabilityActiveDot = (probabilityData: ProbabilityData[]) => (props: CustomDotProps) => {
  if (!props || !probabilityData) {
    return <> </>;
  }

  const leftData = probabilityData.slice(0, props.index + 1);
  const rightData = probabilityData.slice(props.index);

  const totalArea = calculateArea(probabilityData);
  const leftArea = calculateArea(leftData);
  const rightArea = calculateArea(rightData);

  const leftPercentage = totalArea ? ((leftArea / totalArea) * 100).toFixed(0) : 0;
  const rightPercentage = totalArea ? ((rightArea / totalArea) * 100).toFixed(0) : 0;

  return (
    <>
      <path
        d="M320 128L192 256l128 128z"
        fill="#fff"
        transform={`translate(${(props.cx || 0) - 45}, ${(props.cy || 0) - 12.5}) scale(0.03)`}
      />
      <Text
        x={(props.cx || 0) - 5}
        y={props.cy || 0}
        fill="#fff"
        fontSize="12"
        textAnchor="end"
        alignmentBaseline="middle"
      >
        {`${leftPercentage}%`}
      </Text>
      <Text
        x={(props.cx || 0) + 5}
        y={props.cy}
        fill="#fff"
        fontSize="12"
        textAnchor="start"
        alignmentBaseline="middle"
      >
        {`${rightPercentage}%`}
      </Text>
      <path
        d="M192 128l128 128-128 128z"
        fill="#fff"
        transform={`translate(${(props.cx || 0) + 30}, ${(props.cy || 0) - 12.5}) scale(0.03)`}
      />
    </>
  );
};

export const insertZeroCrossingPoints = (data: PayoffGraph[]) => {
  const newData = [];

  const keysToInterpolate = [
    'profit',
    'profitPercent',
    'contractValue',
    'delta',
    'gamma',
    'theta',
    'vega',
    'rho',
  ] as const;

  for (let i = 0; i < data.length - 1; i++) {
    const current = data[i];
    const next = data[i + 1];

    if (current && next && current.stockPrice != null && next.stockPrice != null) {
      newData.push(current);

      if ((current.profit > 0 && next.profit < 0) || (current.profit < 0 && next.profit > 0)) {
        const zeroCrossingStockPrice =
          current.stockPrice +
          ((next.stockPrice - current.stockPrice) * Math.abs(current.profit)) /
            (Math.abs(current.profit) + Math.abs(next.profit));

        const zeroCrossingPoint = keysToInterpolate.reduce((acc, key) => {
          const currentValue = current[key];
          const nextValue = next[key];
          const interpolatedValue =
            currentValue == null || nextValue == null
              ? null
              : currentValue +
                ((nextValue - currentValue) * Math.abs(current.profit)) /
                  (Math.abs(current.profit) + Math.abs(next.profit));

          return {
            ...acc,
            [key]: key === 'profit' ? 0 : interpolatedValue,
          };
        }, {});

        newData.push({
          ...current,
          stockPrice: zeroCrossingStockPrice,
          ...zeroCrossingPoint,
          isZeroCrossing: true,
        });
      }
    }
  }

  if (data.length > 0) {
    newData.push(data[data.length - 1]);
  }

  return newData;
};

export default function PayoffChart({
  data,
  breakEvenPoints,
  activeItem,
  currentStockPrice,
  xMin,
  xMax,
  maxLoss,
  probabilityData,
  animated = true,
  expirationData,
  showExpirationReference = false,
}: PayoffChartProps) {
  const [isHovered, setIsHovered] = useState(false);

  const probabilityDot = probabilityActiveDot(probabilityData);
  const profitLossDot = profitLossActivDot(currentStockPrice || 0);

  // Attach the expiration value of the active metric to each point BEFORE the
  // zero-crossing insertion, so it rides through the same transforms and stays
  // aligned with the live curve.
  const dataWithExpiration = (data || []).map((item, i) => ({
    ...item,
    expirationValue:
      showExpirationReference && expirationData
        ? (expirationData[i] as any)?.[activeItem] ?? null
        : null,
  }));
  const dataWithZeroCrossing = insertZeroCrossingPoints(dataWithExpiration);
  const dataWithPosAndNeg = dataWithZeroCrossing?.map((item) => {
    const result = calculateReturnOnMaxRisk({ profit: item.profit, maxLoss });
    const maxRisk =
      result.percentOfMaxRisk === null ? null : roundToDecimals(result.percentOfMaxRisk, 0);

    return {
      ...item,
      percentOfMaxRisk: maxRisk,
      percentOfMaxRiskReason: result.percentOfMaxRiskReason,
      positiveProfit: item.profit >= 0 ? item.profit : null, // Only positive profits
      negativeProfit: item.profit <= 0 ? item.profit : null, // Only negative profits
      positiveProfitPercent:
        item.profitPercent != null && item.profitPercent >= 0 ? item.profitPercent : null,
      negativeProfitPercent:
        item.profitPercent != null && item.profitPercent <= 0 ? item.profitPercent : null,
      positiveContractValue: item.contractValue >= 0 ? item.contractValue : null, // Only positive profits
      negativeContractValue: item.contractValue <= 0 ? item.contractValue : null, // Only negative profits
      positiveDelta: item.delta >= 0 ? item.delta : null, // Only positive profits
      negativeDelta: item.delta <= 0 ? item.delta : null, // Only negative profits
      positiveGamma: item.gamma >= 0 ? item.gamma : null, // Only positive profits
      negativeGamma: item.gamma <= 0 ? item.gamma : null, // Only negative profits
      positiveTheta: item.theta >= 0 ? item.theta : null, // Only positive profits
      negativeTheta: item.theta <= 0 ? item.theta : null, // Only negative profits
      positiveVega: item.vega >= 0 ? item.vega : null, // Only positive profits
      negativeVega: item.vega <= 0 ? item.vega : null, // Only negative profits
      positiveRho: item.rho >= 0 ? item.rho : null, // Only positive profits
      negativeRho: item.rho <= 0 ? item.rho : null, // Only negative profits
      // zeroProfit: item.profit === 0 ? item.profit : null, // Only zero profits
      positivePercentOfMaxRisk: maxRisk !== null && maxRisk >= 0 ? maxRisk : null,
      negativePercentOfMaxRisk: maxRisk !== null && maxRisk <= 0 ? maxRisk : null,
    };
  });

  const sortedData = dataWithPosAndNeg?.sort((a, b) => a.stockPrice - b.stockPrice);
  const yValues = sortedData?.filter((item) => item.stockPrice > xMin && item.stockPrice < xMax);
  // Include the expiration reference values in the domain so the dotted line is
  // never clipped (at OTM it sits below the live curve at the max-loss floor).
  const refYValues =
    showExpirationReference && expirationData
      ? expirationData
          .filter((item) => item.stockPrice > xMin && item.stockPrice < xMax)
          .map((item: any) => item[activeItem])
      : [];
  const combinedYValues = [
    ...((yValues || []).map((item: any) => item[activeItem]) as number[]),
    ...refYValues,
  ].filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const yMin = combinedYValues.length ? Math.min(...combinedYValues) : -1;
  const yMax = combinedYValues.length ? Math.max(...combinedYValues) : 1;
  const yDistance = Math.max(Math.abs(yMin), Math.abs(yMax));
  const yDomainLower = yMin - 0.2 * Math.abs(yDistance);
  const yDomainUpper = yMax + 0.2 * Math.abs(yDistance);
  const areaGraphKeys = mapAcitveKeyToPositiveAndNegative(activeItem);

  const mergedData = sortedData?.map((item, index) => ({
    ...item,
    probability: probabilityData[index]?.probability || 0,
  }));

  if (
    activeItem === 'percentOfMaxRisk' &&
    dataWithPosAndNeg.length > 0 &&
    dataWithPosAndNeg.every((point) => point.percentOfMaxRisk === null)
  ) {
    const reason = maxRiskPercentUnavailableMessage(dataWithPosAndNeg[0].percentOfMaxRiskReason);
    return (
      <div
        className="flex h-[400px] items-center justify-center p-6 text-sm"
        role="status"
        title={reason}
      >
        N/A — {reason} Select Profit / Loss $ to view the payoff.
      </div>
    );
  }

  if (
    activeItem === 'profitPercent' &&
    data?.length &&
    data.every((point) => point.profitPercent == null)
  ) {
    return (
      <div className="flex h-[400px] items-center justify-center p-6 text-sm" role="status">
        {profitPercentUnavailableMessage(data[0].profitPercentReason)} Select Profit / Loss $ to
        view the payoff.
      </div>
    );
  }

  return (
    <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <ResponsiveContainer width="100%" height={400} className="text-red-900">
        <AreaChart data={mergedData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
          <defs>
            {/* <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#25cf51" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#25cf51" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorLoss" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#7F1C1C" stopOpacity={0} />
            <stop offset="95%" stopColor="#7F1C1C" stopOpacity={0.8} />
          </linearGradient> */}
            <linearGradient id="colorProbability" x1="0" x2="100%" y1="0" y2="0">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.03" />
              <stop offset="50%" stopColor="#fff" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0.03" />
            </linearGradient>
            <mask id="insiderGraphDistMask">
              <rect x="0" y="0" width="100%" height="100%" fill="url(#colorProbability)" />
            </mask>
          </defs>
          <g transform="translate(68 32)" opacity={!isHovered ? 1 : 0}>
            <line x1="0" y1="0" x2="25" y2="0" stroke="#25cf51" strokeWidth="2"></line>
            <text x="30" y="4" fontSize="12" fill="#fff" opacity="0.6">
              Profit
            </text>
          </g>
          <g transform="translate(68 52)" opacity={!isHovered ? 1 : 0}>
            <line x1="0" y1="0" x2="25" y2="0" stroke="#f04662" strokeWidth="2"></line>
            <text x="30" y="4" fontSize="12" fill="#fff" opacity="0.6">
              Loss
            </text>
          </g>
          <g transform="translate(68 72)" opacity={!isHovered ? 1 : 0}>
            <path
              transform="translate(1, -4)"
              d="M1 11 C 6.5 11, 6.5 1, 12.5 1, 18.5 1, 18.5 11, 25 11"
              fill="#468B8B"
              opacity={0.5}
            ></path>
            <text x="30" y="6" fontSize={12} fill="#fff" opacity={0.6}>
              Probability
            </text>
          </g>
          {showExpirationReference && (
            <g transform="translate(68 92)" opacity={!isHovered ? 1 : 0}>
              <line
                x1="0"
                y1="0"
                x2="25"
                y2="0"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeDasharray="4 3"
              ></line>
              <text x="30" y="4" fontSize="12" fill="#fff" opacity="0.6">
                At expiration
              </text>
            </g>
          )}
          <XAxis
            allowDataOverflow
            tickCount={14}
            interval="preserveStartEnd"
            tickSize={12}
            type="number"
            dataKey="stockPrice"
            stroke="#eee"
            tickFormatter={formatTickX}
            fontSize={11}
            minTickGap={1}
            domain={[xMin, xMax]}
          />
          <YAxis
            tickCount={14}
            allowDataOverflow
            type="number"
            minTickGap={1}
            tickSize={12}
            stroke="#eee"
            fontSize={11}
            dataKey={activeItem}
            tickFormatter={(value) => formatTickY(value, activeItem)}
            domain={[yDomainLower, yDomainUpper]}
          />
          <YAxis
            hide
            yAxisId="right"
            orientation="right"
            tickCount={14}
            allowDataOverflow
            type="number"
            minTickGap={1}
            tickSize={12}
            stroke="#05EA8C"
            fontSize={11}
            domain={[-1, Math.max(...probabilityData.map((d) => d.probability)) * 1.2]}
          />
          {breakEvenPoints?.map((price) => {
            const numericPrice = Number(price);
            if (xMin && xMax && numericPrice >= xMin && numericPrice <= xMax) {
              return (
                <ReferenceLine
                  ifOverflow="extendDomain"
                  key={`breakeven-${price}`}
                  x={numericPrice}
                  stroke="#B64EC7"
                  strokeWidth={1}
                  label={{
                    value: `$${numericPrice.toFixed(2)}`,
                    position: 'top',
                    fontSize: '12px',
                    fill: '#B64EC7',
                  }}
                  style={{ zIndex: 1 }}
                />
              );
            }
            return null;
          })}
          <CartesianGrid strokeDasharray="0.1  0.1" strokeWidth={1} opacity={1} />
          <Tooltip content={() => null} cursor={{ strokeWidth: 2 }} />
          <Area
            isAnimationActive={animated}
            yAxisId="right"
            type="linear"
            dataKey="probability"
            strokeWidth={0.1}
            fillOpacity={0.8}
            fill="#468B8B"
            mask="url(#insiderGraphDistMask)"
            activeDot={probabilityDot}
            baseValue={-1}
          />
          {/* Linear (not monotone-cubic) interpolation: a payoff diagram is a
              piecewise-linear function, and cubic smoothing rounds the sharp
              hockey-stick corners at each strike — most visibly at expiration.
              With strikes sampled explicitly (strategyKernel.sweepCurve), linear
              segments bend exactly at the strike, giving true sharp corners. */}
          <Area
            isAnimationActive={animated}
            type="linear"
            dataKey={areaGraphKeys[0]}
            stroke="#25cf51"
            strokeWidth={3}
            fillOpacity={0}
            stackId={1}
            activeDot={profitLossDot}
          />
          <Area
            isAnimationActive={animated}
            type="linear"
            dataKey={areaGraphKeys[1]}
            stroke="#f04662"
            strokeWidth={3}
            fillOpacity={0}
            stackId={1}
            activeDot={profitLossDot}
          />
          {showExpirationReference && (
            <Area
              isAnimationActive={animated}
              type="linear"
              dataKey="expirationValue"
              stroke="#9CA3AF"
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="none"
              fillOpacity={0}
              dot={false}
              activeDot={false}
            />
          )}
          <ReferenceLine y={0} stroke="#fff" strokeWidth={1.5} />

          {mergedData?.map((item, index) => {
            if (item?.isZeroCrossing) {
              return (
                <ReferenceDot
                  key={`zero-crossing-${index}`}
                  x={item.stockPrice}
                  y={item.profit}
                  r={6}
                  fill="#B64EC7"
                  stroke="#eeeeee"
                  strokeWidth={2}
                />
              );
            }
            return null;
          })}

          {!!currentStockPrice && (
            <ReferenceLine
              ifOverflow="extendDomain"
              x={currentStockPrice}
              stroke="#fff"
              strokeWidth={1.5}
              strokeDasharray="2 2"
              fill="#fff"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
